import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, ArrowRightLeft, History, Warehouse as WarehouseIcon, Package, Box, AlertTriangle, XCircle, MoreHorizontal, Check } from 'lucide-react';
import { StatCard, PageHeader } from '@/cajita/ui';
import { CxDialog } from '@/cajita/overlay';
import { listProducts } from '@/features/products/products.api';
import { useWarehouses, useCreateWarehouse, useStock, useCreateMovement, useKardex, type MovementType } from './inventory.api';

const money = (n: number) => 'S/ ' + n.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const qty = (n: number) => new Intl.NumberFormat('es-PE', { maximumFractionDigits: 4 }).format(n);

const TYPE_LABEL: Record<string, string> = {
  IN: 'Ingreso',
  OUT: 'Salida',
  ADJUST: 'Ajuste',
  TRANSFER: 'Transferencia',
  TRANSFER_IN: 'Transf. entrada',
  TRANSFER_OUT: 'Transf. salida',
};


export function InventoryPage(): JSX.Element {
  const { data: warehouses } = useWarehouses();
  const [tab, setTab] = useState<'stock' | 'almacenes'>('stock');
  const [whId, setWhId] = useState<string>('');
  const [movOpen, setMovOpen] = useState(false);
  const [kardexProduct, setKardexProduct] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    const first = warehouses?.[0];
    if (!whId && first) setWhId(first.id);
  }, [warehouses, whId]);

  const { data: stock, isLoading } = useStock(whId || undefined);
  const rows = stock ?? [];
  const totalValue = useMemo(() => rows.reduce((s, r) => s + r.value, 0), [rows]);
  const low = rows.filter((r) => r.quantity > 0 && r.quantity < 10).length;
  const out = rows.filter((r) => r.quantity <= 0).length;

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Inventario"
          sub="Control de existencias, kardex y almacenes"
          actions={
            <button className="btn btn-primary" onClick={() => setMovOpen(true)}>
              <ArrowRightLeft size={17} />
              Nuevo movimiento
            </button>
          }
        />

        <div className="kpi-grid" style={{ marginBottom: 20 }}>
          <StatCard label="Valor de inventario" value={money(totalValue)} icon={<Package size={19} />} accent="#8B5CF6" />
          <StatCard label="SKUs en almacén" value={String(rows.length)} icon={<Box size={19} />} accent="#3B82F6" />
          <StatCard label="Stock bajo" value={String(low)} icon={<AlertTriangle size={19} />} accent="#F59E0B" />
          <StatCard label="Sin stock" value={String(out)} icon={<XCircle size={19} />} accent="#EF4444" />
        </div>

        <div className="row gap4" style={{ borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
          {([
            ['stock', 'Existencias', rows.length],
            ['almacenes', 'Almacenes', warehouses?.length ?? 0],
          ] as const).map(([id, label, count]) => {
            const on = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                style={{
                  border: 'none',
                  background: 'transparent',
                  padding: '10px 14px',
                  fontSize: 14,
                  fontWeight: 600,
                  color: on ? 'var(--ink)' : 'var(--muted)',
                  borderBottom: on ? '2px solid var(--primary)' : '2px solid transparent',
                  marginBottom: -1,
                }}
              >
                {label}
                <span className="badge badge-gray" style={{ marginLeft: 7 }}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {tab === 'stock' && (
          <div className="card">
            <div className="tbl-toolbar">
              <div className="row aic gap8">
                <span className="t-sm muted">Almacén:</span>
                <select className="select" style={{ width: 220 }} value={whId} onChange={(e) => setWhId(e.target.value)}>
                  {(warehouses ?? []).map((w) => (
                    <option key={w.id} value={w.id}>
                      {w.code} · {w.name}
                    </option>
                  ))}
                </select>
              </div>
              <span className="mla t-sm">
                <span className="muted">Valor: </span>
                <span className="tnum fw7">{money(totalValue)}</span>
              </span>
            </div>

            {isLoading ? (
              <div style={{ padding: '8px 0' }}>
                {Array.from({ length: 7 }).map((_, r) => (
                  <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
                    {Array.from({ length: 5 }).map((_, c) => (
                      <div key={c} className="sk" style={{ height: 12, flex: c === 1 ? 2 : 1, opacity: 1 - r * 0.08 }} />
                    ))}
                  </div>
                ))}
              </div>
            ) : rows.length === 0 ? (
              <div className="empty">
                <div className="empty-ico">
                  <WarehouseIcon size={28} />
                </div>
                <div className="muted">Sin existencias en este almacén. Registra un ingreso.</div>
              </div>
            ) : (
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>SKU</th>
                      <th>Producto</th>
                      <th className="right">Stock</th>
                      <th className="right">Costo prom.</th>
                      <th className="right">Valor</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.productId} style={{ cursor: 'pointer' }} onClick={() => setKardexProduct({ id: r.productId, name: r.name })}>
                        <td className="tnum muted">{r.sku}</td>
                        <td className="strong">{r.name}</td>
                        <td className="right">
                          <span className={`badge badge-${r.quantity <= 0 ? 'red' : r.quantity < 10 ? 'amber' : 'green'}`}>
                            {qty(r.quantity)} {r.unitCode === 'KGM' ? 'kg' : 'und'}
                          </span>
                        </td>
                        <td className="right tnum muted">{money(r.avgCost)}</td>
                        <td className="right tnum strong">{money(r.value)}</td>
                        <td className="right" onClick={(e) => e.stopPropagation()}>
                          <button className="btn btn-ghost btn-sm" onClick={() => setKardexProduct({ id: r.productId, name: r.name })}>
                            <History size={15} />
                            Kardex
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'almacenes' && <WarehousesPanel />}
      </div>

      {movOpen && (
        <MovementDialog
          open={movOpen}
          onClose={() => setMovOpen(false)}
          warehouses={(warehouses ?? []).map((w) => ({ id: w.id, label: `${w.code} · ${w.name}` }))}
          defaultWarehouseId={whId}
        />
      )}
      {kardexProduct && <KardexDialog product={kardexProduct} warehouseId={whId || undefined} onClose={() => setKardexProduct(null)} />}
    </div>
  );
}

function WarehousesPanel(): JSX.Element {
  const { data: warehouses } = useWarehouses();
  const create = useCreateWarehouse();
  const [f, setF] = useState({ code: '', name: '', address: '' });
  const [adding, setAdding] = useState(false);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      { code: f.code.trim(), name: f.name.trim(), address: f.address.trim() || undefined },
      { onSuccess: () => { setF({ code: '', name: '', address: '' }); setAdding(false); } },
    );
  };

  return (
    <>
      <div className="grid" style={{ gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 16 }}>
        {(warehouses ?? []).map((w) => (
          <div key={w.id} className="card card-pad fade-in">
            <div className="row between aic" style={{ marginBottom: 14 }}>
              <div style={{ width: 44, height: 44, borderRadius: 12, background: 'var(--canvas)', display: 'grid', placeItems: 'center', color: 'var(--ink-2)' }}>
                <WarehouseIcon size={22} />
              </div>
              <button className="btn btn-ghost btn-sm btn-icon">
                <MoreHorizontal size={16} />
              </button>
            </div>
            <div className="t-h2 fw7">
              {w.name} {w.isDefault && <span className="t-xs muted">(principal)</span>}
            </div>
            <div className="t-sm muted">
              {w.code}
              {w.address ? ` · ${w.address}` : ''}
            </div>
          </div>
        ))}
        <button
          className="card card-pad"
          onClick={() => setAdding((v) => !v)}
          style={{ display: 'grid', placeItems: 'center', minHeight: 160, border: '1.5px dashed var(--border)', cursor: 'pointer', background: 'transparent' }}
        >
          <div className="col aic gap8 muted">
            <div style={{ width: 40, height: 40, borderRadius: 11, background: 'var(--canvas)', display: 'grid', placeItems: 'center' }}>
              <Plus size={20} />
            </div>
            <span className="fw6 t-sm">Nuevo almacén</span>
          </div>
        </button>
      </div>

      {adding && (
        <form onSubmit={submit} className="card card-pad" style={{ marginTop: 16, maxWidth: 560 }}>
          <div className="t-h2 fw7" style={{ marginBottom: 14 }}>
            Nuevo almacén
          </div>
          <div className="grid" style={{ gridTemplateColumns: '120px 1fr', gap: 14, marginBottom: 14 }}>
            <div className="field">
              <label className="label">Código</label>
              <input className="input" value={f.code} maxLength={20} required onChange={(e) => setF((s) => ({ ...s, code: e.target.value }))} />
            </div>
            <div className="field">
              <label className="label">Nombre</label>
              <input className="input" value={f.name} required onChange={(e) => setF((s) => ({ ...s, name: e.target.value }))} />
            </div>
          </div>
          <div className="field" style={{ marginBottom: 16 }}>
            <label className="label">Dirección (opcional)</label>
            <input className="input" value={f.address} onChange={(e) => setF((s) => ({ ...s, address: e.target.value }))} />
          </div>
          <div className="row gap10">
            <button type="button" className="btn btn-secondary" onClick={() => setAdding(false)}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary" disabled={create.isPending}>
              <Plus size={17} />
              {create.isPending ? 'Creando…' : 'Crear almacén'}
            </button>
          </div>
        </form>
      )}
    </>
  );
}

function MovementDialog({
  open,
  onClose,
  warehouses,
  defaultWarehouseId,
}: {
  open: boolean;
  onClose: () => void;
  warehouses: Array<{ id: string; label: string }>;
  defaultWarehouseId: string;
}): JSX.Element {
  const create = useCreateMovement();
  const { data: products } = useQuery({ queryKey: ['products-for-inventory'], queryFn: () => listProducts({ pageSize: 200 }) });
  const [f, setF] = useState({
    type: 'IN' as MovementType,
    productId: '',
    warehouseId: defaultWarehouseId,
    toWarehouseId: '',
    quantity: '',
    unitCost: '',
    reason: '',
    reference: '',
    lotCode: '',
    expiryDate: '',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((s) => ({ ...s, [k]: e.target.value }));

  const showCost = f.type === 'IN' || f.type === 'ADJUST';
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        type: f.type,
        productId: f.productId,
        warehouseId: f.warehouseId,
        toWarehouseId: f.type === 'TRANSFER' ? f.toWarehouseId : undefined,
        quantity: Number(f.quantity),
        unitCost: showCost && f.unitCost ? Number(f.unitCost) : undefined,
        reason: f.reason || undefined,
        reference: f.reference || undefined,
        lotCode: f.lotCode || undefined,
        expiryDate: f.expiryDate || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  if (!open) return <></>;

  return (
    <CxDialog
      title="Movimiento de inventario"
      sub="Ingreso, salida, ajuste o transferencia"
      icon={<ArrowRightLeft size={20} />}
      iconTone="primary"
      width={520}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" type="submit" form="inv-mov" disabled={create.isPending}>
            <Check size={18} />
            {create.isPending ? 'Guardando…' : 'Registrar'}
          </button>
        </>
      }
    >
      <form id="inv-mov" onSubmit={submit} className="col gap14">
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label className="label">Tipo</label>
            <select className="select" value={f.type} onChange={set('type')}>
              <option value="IN">Ingreso</option>
              <option value="OUT">Salida / merma</option>
              <option value="ADJUST">Ajuste (conteo)</option>
              <option value="TRANSFER">Transferencia</option>
            </select>
          </div>
          <div className="field">
            <label className="label">{f.type === 'ADJUST' ? 'Cantidad final' : 'Cantidad'}</label>
            <input className="input tnum" type="number" step="any" min="0" value={f.quantity} required onChange={set('quantity')} />
          </div>
        </div>
        <div className="field">
          <label className="label">Producto</label>
          <select className="select" value={f.productId} onChange={set('productId')} required>
            <option value="">Selecciona…</option>
            {(products?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.sku} · {p.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label className="label">{f.type === 'TRANSFER' ? 'Almacén origen' : 'Almacén'}</label>
            <select className="select" value={f.warehouseId} onChange={set('warehouseId')} required>
              {warehouses.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.label}
                </option>
              ))}
            </select>
          </div>
          {f.type === 'TRANSFER' && (
            <div className="field">
              <label className="label">Almacén destino</label>
              <select className="select" value={f.toWarehouseId} onChange={set('toWarehouseId')} required>
                <option value="">Selecciona…</option>
                {warehouses.filter((w) => w.id !== f.warehouseId).map((w) => (
                  <option key={w.id} value={w.id}>
                    {w.label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {showCost && (
            <div className="field">
              <label className="label">Costo unitario (opcional)</label>
              <input className="input tnum" type="number" step="any" min="0" value={f.unitCost} onChange={set('unitCost')} />
            </div>
          )}
        </div>
        <div className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div className="field">
            <label className="label">Lote (opcional)</label>
            <input className="input" value={f.lotCode} onChange={set('lotCode')} />
          </div>
          <div className="field">
            <label className="label">Vencimiento (opcional)</label>
            <input className="input" type="date" value={f.expiryDate} onChange={set('expiryDate')} />
          </div>
        </div>
        <div className="field">
          <label className="label">Motivo / referencia (opcional)</label>
          <input className="input" value={f.reason} placeholder="Ej: compra OC-001, merma, conteo mensual" onChange={set('reason')} />
        </div>
      </form>
    </CxDialog>
  );
}

function KardexDialog({ product, warehouseId, onClose }: { product: { id: string; name: string }; warehouseId?: string; onClose: () => void }): JSX.Element {
  const { data, isLoading } = useKardex(product.id, warehouseId);
  return (
    <CxDialog title="Kardex" sub={product.name} icon={<History size={20} />} iconTone="primary" width={680} onClose={onClose}>
      <div className="tbl-wrap" style={{ maxHeight: '56vh', overflowY: 'auto' }}>
        <table className="tbl dense">
          <thead>
            <tr>
              <th>Fecha</th>
              <th>Movimiento</th>
              <th className="right">Cant.</th>
              <th className="right">Saldo</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: '20px' }}>
                  Cargando…
                </td>
              </tr>
            )}
            {!isLoading && (data ?? []).length === 0 && (
              <tr>
                <td colSpan={4} className="muted" style={{ textAlign: 'center', padding: '20px' }}>
                  Sin movimientos.
                </td>
              </tr>
            )}
            {(data ?? []).map((m) => {
              const cls = m.type.includes('IN') ? 'badge-green' : m.type === 'ADJUST' ? 'badge-yellow' : 'badge-amber';
              return (
                <tr key={m.id}>
                  <td className="tnum muted" style={{ whiteSpace: 'nowrap' }}>
                    {new Date(m.createdAt).toLocaleDateString('es-PE')}
                  </td>
                  <td>
                    <span className={`badge ${cls}`}>{TYPE_LABEL[m.type] ?? m.type}</span>{' '}
                    <span className="t-sm muted">
                      {m.reason ?? ''}
                      {m.lotCode ? ` · lote ${m.lotCode}` : ''}
                    </span>
                  </td>
                  <td className={`right tnum fw6 ${Number(m.quantity) < 0 ? 'neg' : 'pos'}`}>{qty(m.quantity)}</td>
                  <td className="right tnum strong">{qty(m.balanceAfter)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </CxDialog>
  );
}
